import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Lightbulb, ThumbsUp, Lock } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function JobDetail() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  useEffect(() => { api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => nav("/jobs")); }, [jobId, nav]);
  if (!job) return <Loader />;

  return (
    <div className="max-w-3xl mx-auto">
      <Button variant="ghost" className="rounded-full mb-4 -ml-2" onClick={() => nav("/jobs")} data-testid="job-back"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Jobs</Button>
      <div className="bg-white border border-border rounded-2xl shadow-sm p-6 sm:p-10">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">{job.title}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {job.category && <Badge variant="secondary">{job.category}</Badge>}
              {job.featured && <Badge className="bg-warning/10 text-warning">★ Featured</Badge>}
            </div>
          </div>
          <p className="text-xl font-heading font-bold text-teal">{job.rate}</p>
        </div>

        {job.skills?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {job.skills.map((s, i) => <span key={i} className="text-xs bg-secondary px-2.5 py-1 rounded-full">{s}</span>)}
          </div>
        )}

        {job.summary && <p className="text-muted-foreground mt-5 leading-relaxed whitespace-pre-wrap">{job.summary}</p>}

        {job.analysis && (
          <div className="mt-6 p-5 rounded-xl bg-teal/5 border border-teal/20" data-testid="job-analysis">
            <p className="font-semibold flex items-center gap-2"><Lightbulb className="w-5 h-5 text-teal" /> Coach Reyland's Analysis</p>
            <p className="text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">{job.analysis}</p>
          </div>
        )}
        {job.why_recommended && (
          <div className="mt-4 p-5 rounded-xl bg-brandgreen/5 border border-brandgreen/20" data-testid="job-why">
            <p className="font-semibold flex items-center gap-2"><ThumbsUp className="w-5 h-5 text-brandgreen" /> Why It's Recommended</p>
            <p className="text-muted-foreground mt-2 leading-relaxed whitespace-pre-wrap">{job.why_recommended}</p>
          </div>
        )}

        {job.apply_locked ? (
          <div className="mt-8" data-testid="apply-locked">
            <div className="w-full rounded-full bg-secondary text-muted-foreground h-12 flex items-center justify-center font-semibold border border-border cursor-not-allowed">
              <Lock className="w-4 h-4 mr-2" /> Apply Locked — Premium/Niche Access Required
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">This job pays $6/hr or more. Unlock a niche course or premium access to apply. You can still read the full analysis above.</p>
          </div>
        ) : (
          <a href={job.upwork_url} target="_blank" rel="noreferrer" className="block mt-8" data-testid="apply-now">
            <Button size="lg" className="w-full rounded-full bg-teal hover:bg-teal/90 h-12">
              Apply Now on Upwork <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </a>
        )}
        {!job.apply_locked && <p className="text-xs text-muted-foreground text-center mt-2">Opens the original job posting on Upwork in a new tab.</p>}
      </div>
    </div>
  );
}
